import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { QueryKeys } from 'src/constants';
import ApiService from 'src/modules/api-service';
import { SupportedCurrencyCode } from 'src/modules/currency';

export type ExchangeRates = {
  [k in SupportedCurrencyCode]: {
    [u in SupportedCurrencyCode]: number;
  };
};

// Fallback exchange rates for when API returns empty or incomplete data.
const FALLBACK_EXCHANGE_RATES: ExchangeRates = {
  EUR: {
    EUR: 1.0,
    RON: 5.0,
    CZK: 24.3902,
    SEK: 10.8696,
    PLN: 4.7619,
    HUF: 416.6667,
    CHF: 0.9434,
    DKK: 7.4627,
    NOK: 11.6279,
  },
  RON: {
    EUR: 0.2,
    RON: 1.0,
    CZK: 4.878,
    SEK: 2.1739,
    PLN: 0.9524,
    HUF: 83.3333,
    CHF: 0.1887,
    DKK: 1.4925,
    NOK: 2.3256,
  },
  CZK: {
    EUR: 0.041,
    RON: 0.205,
    CZK: 1.0,
    SEK: 0.4457,
    PLN: 0.1952,
    HUF: 17.0833,
    CHF: 0.0387,
    DKK: 0.306,
    NOK: 0.4767,
  },
  PLN: { EUR: 0.21, RON: 1.05, CZK: 5.122, SEK: 2.2826, PLN: 1.0, HUF: 87.5, CHF: 0.1981, DKK: 1.5672, NOK: 2.4419 },
  HUF: {
    EUR: 0.0024,
    RON: 0.012,
    CZK: 0.0585,
    SEK: 0.0261,
    PLN: 0.0114,
    HUF: 1.0,
    CHF: 0.0023,
    DKK: 0.0179,
    NOK: 0.0279,
  },
  SEK: {
    EUR: 0.0909,
    RON: 0.4483,
    CZK: 2.2353,
    SEK: 1.0,
    PLN: 0.4392,
    HUF: 38.1376,
    CHF: 0.0868,
    DKK: 0.6866,
    NOK: 1.0698,
  },
  CHF: {
    EUR: 1.06,
    RON: 5.3,
    CZK: 25.8537,
    SEK: 11.5217,
    PLN: 5.0476,
    HUF: 441.6667,
    CHF: 1.0,
    DKK: 7.9103,
    NOK: 12.3256,
  },
  DKK: {
    EUR: 0.134,
    RON: 0.67,
    CZK: 3.268,
    SEK: 1.4565,
    PLN: 0.6382,
    HUF: 55.8333,
    CHF: 0.1264,
    DKK: 1.0,
    NOK: 1.5582,
  },
  NOK: {
    EUR: 0.086,
    RON: 0.43,
    CZK: 2.0976,
    SEK: 0.9348,
    PLN: 0.4096,
    HUF: 35.8333,
    CHF: 0.0811,
    DKK: 0.6417,
    NOK: 1.0,
  },
};

const SUPPORTED_CURRENCIES = Object.keys(FALLBACK_EXCHANGE_RATES) as SupportedCurrencyCode[];

// There is a 0.1% chance for the exchange rates to be empty or incomplete.
// In order to prevent chaos we fallback to legacy conversion factors.
const patchExchangeRates = (exchangeRates: ExchangeRates): ExchangeRates => {
  if (isEmpty(exchangeRates)) {
    // eslint-disable-next-line no-console
    console.warn('No exchange rates available! Fallback applied!');
    return FALLBACK_EXCHANGE_RATES;
  }

  // Fill in any missing currencies or individual rates with fallback values
  return SUPPORTED_CURRENCIES.reduce(
    (patched, from) => {
      const fallbackRates = FALLBACK_EXCHANGE_RATES[from];
      const existingRates = patched[from];

      return {
        ...patched,
        [from]: isEmpty(existingRates) ? fallbackRates : { ...fallbackRates, ...existingRates },
      };
    },
    { ...exchangeRates },
  );
};

export const useExchangeRates = () =>
  useQuery(
    [QueryKeys.exchangeRates],
    () =>
      ApiService.calcPrice
        .priceCalculationControllerGetExchangeRates()
        .then((res) => patchExchangeRates(res.data as ExchangeRates)),
    {
      suspense: true,
      staleTime: 1000 * 60 * 60 * 24, // 1d - exchange rates get updated max once per day
    },
  );
